// SpreadJS IO plugins must be imported for export() method to work
import '@mescius/spread-sheets-io';
import '@mescius/spread-excelio';

import type { SheetSpec } from '@meridian-ssa/shared';
import * as GC from '@mescius/spread-sheets';

import { config } from '@/config';
import { logError } from '@/lib/logger';
import {
  addNamedRanges,
  applyCellStyle,
  applyConditionalFormats,
  parseA1,
  setCellContent,
} from '@/lib/spreadjs/utils';

if (config.spreadJSLicenseKey) {
  GC.Spread.Sheets.LicenseKey = config.spreadJSLicenseKey;
}

export async function sheetSpecToExcelBlob(
  sheetSpec: SheetSpec
): Promise<Blob | null> {
  if (!sheetSpec || !sheetSpec.sheets || sheetSpec.sheets.length === 0) {
    return null;
  }

  try {
    const workbook = new GC.Spread.Sheets.Workbook();
    workbook.clearSheets();

    const sheets: any[] = [];
    sheetSpec.sheets.forEach((sheetData: any, sheetIndex: number) => {
      const sheet = new GC.Spread.Sheets.Worksheet(sheetData.name);
      workbook.addSheet(sheetIndex, sheet);
      sheets.push(sheet);
    });

    sheetSpec.sheets.forEach((sheetData: any, sheetIndex: number) => {
      const sheet = sheets[sheetIndex];
      sheet.suspendPaint();

      let maxRow = 0;
      let maxCol = 0;

      sheetData.cells.forEach((cell: any) => {
        try {
          const { row, col } = parseA1(cell.a1);
          maxRow = Math.max(maxRow, row);
          maxCol = Math.max(maxCol, col);
          setCellContent(sheet, cell, row, col);
          applyCellStyle(sheet, cell, row, col);
        } catch {
          // Skip failed cells
        }
      });

      if (sheetData.namedRanges) {
        addNamedRanges(workbook, sheetData.namedRanges, sheetData.name);
      }

      if (sheetData.conditionalFormats) {
        applyConditionalFormats(sheet, sheetData.conditionalFormats);
      }

      for (let col = 0; col <= maxCol; col++) {
        sheet.autoFitColumn(col);
      }
      for (let row = 0; row <= maxRow; row++) {
        sheet.autoFitRow(row);
      }

      sheet.resumePaint();
    });

    workbook.calculate();

    const blob = await new Promise<Blob>((resolve, reject) => {
      workbook.export(
        (blob: Blob) => resolve(blob),
        (error: any) => reject(new Error(error.errorMessage || 'Export failed')),
        { fileType: GC.Spread.Sheets.FileType.excel }
      );
    });

    workbook.destroy();
    return blob;
  } catch (error) {
    logError('Failed to convert SheetSpec to Excel blob', error);
    return null;
  }
}

/**
 * Download a SheetSpec as an Excel file using SpreadJS export.
 * This preserves all formatting (colors, fonts, borders) unlike SheetJS.
 */
export async function downloadSheetSpecAsExcelSpreadJS(
  sheetSpec: SheetSpec,
  filename: string = 'spreadsheet.xlsx'
): Promise<void> {
  if (!sheetSpec || !sheetSpec.sheets) {
    throw new Error('Invalid SheetSpec: missing sheets');
  }

  try {
    // Create a headless workbook
    const workbook = new GC.Spread.Sheets.Workbook();

    // Remove default sheet
    workbook.clearSheets();

    // Create all sheets first (to avoid #REF! errors for cross-sheet references)
    const sheets: any[] = [];
    sheetSpec.sheets.forEach((sheetData, sheetIndex) => {
      const sheet = new GC.Spread.Sheets.Worksheet(sheetData.name);
      workbook.addSheet(sheetIndex, sheet);
      sheets.push(sheet);
    });

    // Populate cells with data AND styles
    sheetSpec.sheets.forEach((sheetData, sheetIndex) => {
      const sheet = sheets[sheetIndex];

      // Suspend painting for performance
      sheet.suspendPaint();

      // Track max row/col for auto-fit
      let maxRow = 0;
      let maxCol = 0;

      // Load cells with content and styles
      sheetData.cells.forEach(cell => {
        try {
          const { row, col } = parseA1(cell.a1);

          // Track dimensions
          maxRow = Math.max(maxRow, row);
          maxCol = Math.max(maxCol, col);

          // Set cell content (value or formula)
          setCellContent(sheet, cell, row, col);

          // Apply cell styling (fill, font, borders)
          applyCellStyle(sheet, cell, row, col);
        } catch (e) {
          logError(`Failed to load cell ${cell.a1}`, e);
        }
      });

      // Add named ranges
      if (sheetData.namedRanges) {
        addNamedRanges(workbook, sheetData.namedRanges, sheetData.name);
      }

      // Apply conditional formatting rules
      if (sheetData.conditionalFormats) {
        applyConditionalFormats(sheet, sheetData.conditionalFormats);
      }

      // Auto-fit columns to content width
      for (let col = 0; col <= maxCol; col++) {
        sheet.autoFitColumn(col);
      }

      // Auto-fit rows to content height
      for (let row = 0; row <= maxRow; row++) {
        sheet.autoFitRow(row);
      }

      sheet.resumePaint();
    });

    // Force recalculation
    workbook.calculate();

    // Export using SpreadJS native export (same as desktop app)
    const blob = await new Promise<Blob>((resolve, reject) => {
      workbook.export(
        (blob: Blob) => {
          resolve(blob);
        },
        (error: any) => {
          logError('SpreadJS export error', error);
          reject(
            new Error(error.errorMessage || 'Failed to export Excel file')
          );
        },
        {
          fileType: GC.Spread.Sheets.FileType.excel,
        }
      );
    });

    // Trigger browser download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Clean up
    workbook.destroy();
  } catch (error: any) {
    logError('Failed to export SheetSpec as Excel', error);
    throw error;
  }
}
